import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import {
  BrandsTableResponse,
  BrandResponse,
} from 'src/lib/services/api/brand-api/brand-client.types';
import { useBrandsQuery } from 'src/api/queries/useBrandsQuery/useBrandsQuery';
import { getBrandIdFromWebviewConfig } from 'src/utils/webview-detection';
import { WebViewAuthService } from 'src/services/Auth/WebView.auth';
import * as brandClient from 'src/lib/services/api/brand-api/brand-client';
import { generatePath, useLocation, useNavigate } from 'react-router-dom';
import { appRoutes } from 'src/routes';

interface BrandSelectionContextType {
  brands: BrandsTableResponse[];
  brandsLoading: boolean;
  selectedBrand: BrandsTableResponse | null;
  setSelectedBrand: (brand: BrandsTableResponse | null) => void;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  isInitialized: boolean;
  // Optimization: Track whether selected brand has monthly_video_count
  selectedBrandHasVideoCount: boolean | null;
  selectedBrandVideoCount: string | null;
}

const BrandSelectionContext = createContext<BrandSelectionContextType | undefined>(undefined);

interface BrandSelectionProviderProps {
  children: ReactNode;
}

// Cookie utilities for secure brand storage
const BRAND_COOKIE_NAME = 'selectedBrandId';
const COOKIE_OPTIONS = {
  sameSite: 'lax' as const, // Changed from 'strict' to 'lax' for better compatibility
  httpOnly: false, // Need to read from JS
  maxAge: 60 * 60 * 24 * 30, // 30 days
};

const setCookie = (name: string, value: string, options: typeof COOKIE_OPTIONS) => {
  const cookieString = `${name}=${value}; max-age=${options.maxAge}; path=/; samesite=${options.sameSite}`;
  document.cookie = cookieString;

  setTimeout(() => {
    const verification = getCookie(name);
  }, 100);
};

const getCookie = (name: string): string | null => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    const result = parts.pop()?.split(';').shift() || null;
    return result;
  }
  return null;
};

const deleteCookie = (name: string) => {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
};

export const BrandSelectionProvider: React.FC<BrandSelectionProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const getIsLocationBrandPage = () => {
    return location.pathname.includes('/brands/brand/');
  };

  const [selectedBrand, setSelectedBrandState] = useState<BrandsTableResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  // Optimization: Track monthly_video_count for selected brand
  const [selectedBrandHasVideoCount, setSelectedBrandHasVideoCount] = useState<boolean | null>(
    null,
  );
  const [selectedBrandVideoCount, setSelectedBrandVideoCount] = useState<string | null>(null);
  const manuallySetBrandRef = useRef<string | null>(null); // Track manually set brand ID
  // because of pagination, we asume that user never has more than 100 brands
  const {
    data: brandsData,
    isLoading: brandsLoading,
    isFetched: hasInitialized,
    refetch: refetchBrands,
  } = useBrandsQuery({
    limit: 100,
  });
  const brands = brandsData?.items || [];

  const setSelectedBrand = (brand: BrandsTableResponse | null) => {
    setSelectedBrandState(brand);
    // Track that this brand was manually set to prevent auto-override
    manuallySetBrandRef.current = brand?.id || null;

    if (brand) {
      setCookie(BRAND_COOKIE_NAME, brand.id, COOKIE_OPTIONS);
      if (WebViewAuthService.isWebViewAuthEnabled()) {
        window.ReactNativeWebView?.postMessage(
          JSON.stringify({
            type: 'CHANGE_BRAND',
            brandId: brand.id,
            secure: true,
          }),
        );
      }
      // If we are on a brand page, navigate to the new brand
      if (getIsLocationBrandPage()) {
        navigate(
          {
            pathname: generatePath(appRoutes.brand, { brandId: brand.id }),
            search: location.search, // Preserve current search parameters including tab
          },
          { replace: WebViewAuthService.isWebViewAuthEnabled() },
        );
      }
    } else {
      deleteCookie(BRAND_COOKIE_NAME);
    }
  };

  useEffect(() => {
    if (!brandsData || brands.length === 0 || WebViewAuthService.isWebViewAuthEnabled()) {
      return;
    }

    const savedBrandId = getCookie(BRAND_COOKIE_NAME);

    // If we just manually set a brand, don't override it unless enough time has passed
    // This prevents race conditions when refetchBrands() is called after brand creation
    if (
      manuallySetBrandRef.current &&
      selectedBrand &&
      selectedBrand.id === manuallySetBrandRef.current
    ) {
      // Check if the manually set brand now appears in the list
      const manualBrand = brands.find((brand) => brand.id === manuallySetBrandRef.current);
      if (manualBrand) {
        // Great! The manually set brand is now in the list, update with full data
        setSelectedBrandState(manualBrand);
        manuallySetBrandRef.current = null; // Clear the ref since we're now synced
      } else {
        // Actively fetch until the brand appears - this handles post-onboarding scenario
        const fetchUntilBrandExists = async () => {
          const maxAttempts = 10;
          const delayMs = 1000;

          for (let attempt = 1; attempt <= maxAttempts; attempt++) {
            const result = await refetchBrands();
            const updatedBrands = result.data?.items || [];

            // Check if the brand now exists in the fetched data
            const foundBrand = updatedBrands.find(
              (brand) => brand.id === manuallySetBrandRef.current,
            );

            if (foundBrand) {
              setSelectedBrandState(foundBrand);
              manuallySetBrandRef.current = null; // Clear the ref since we're now synced
              return; // Break out of the loop
            }

            if (attempt < maxAttempts) {
              await new Promise((resolve) => setTimeout(resolve, delayMs));
            }
          }
        };

        // Don't await - let it run in background
        fetchUntilBrandExists();
      }
      // If not found yet, keep the manually set brand and don't override
      return;
    }

    if (savedBrandId) {
      const savedBrand = brands.find((brand) => brand.id === savedBrandId);
      if (savedBrand) {
        setSelectedBrandState(savedBrand);
        manuallySetBrandRef.current = null; // Clear ref since we're using cookie selection
        return;
      } else {
        deleteCookie(BRAND_COOKIE_NAME);
      }
    }

    if (brands.length > 0) {
      setSelectedBrand(brands[0]);
    }
  }, [brands, brandsData, selectedBrand]);

  // Sync selected brand when brands data changes (e.g., after brand updates)
  useEffect(() => {
    if (!selectedBrand || !brands.length) {
      return;
    }

    // Find the updated version of the currently selected brand
    const updatedBrand = brands.find((brand) => brand.id === selectedBrand.id);

    if (updatedBrand) {
      // Brand still exists - update with latest data if name changed
      if (updatedBrand.name !== selectedBrand.name) {
        setSelectedBrandState(updatedBrand);
      }
    } else {
      // Brand no longer exists - but check if this might be a newly created brand
      // that hasn't appeared in the list yet. Only fallback if we're sure it was deleted.
      const savedBrandId = getCookie(BRAND_COOKIE_NAME);
      if (savedBrandId === selectedBrand.id) {
        return;
      }
      // Brand was actually deleted - fallback to first brand
      if (brands.length > 0) {
        setSelectedBrand(brands[0]);
      } else {
        setSelectedBrand(null);
      }
    }
  }, [brands, selectedBrand]);

  const setWebviewBrand = async () => {
    let brandsData = brands;

    const savedBrandId = getCookie(BRAND_COOKIE_NAME);
    const webviewBrandId = getBrandIdFromWebviewConfig();

    if (!brandsData?.some((b) => b.id === webviewBrandId || b.id === savedBrandId)) {
      const res = await refetchBrands();
      if (res.data?.items) brandsData = res.data.items;
    }

    const savedBrand = brandsData?.find((b) => b.id === savedBrandId);
    const webviewBrand = brandsData?.find((b) => b.id === webviewBrandId);

    if (
      webviewBrand &&
      (webviewBrandId !== savedBrandId ||
        (getIsLocationBrandPage() && !location.pathname.includes(webviewBrandId)))
    ) {
      setSelectedBrand(webviewBrand);
      return;
    }
    if (savedBrand) {
      setSelectedBrandState(savedBrand);
      return;
    }
    if (brandsData?.length > 0) {
      setSelectedBrand(brandsData[0]);
    }
  };

  useEffect(() => {
    if (!WebViewAuthService.isWebViewAuthEnabled() || !brands?.length) return;

    setWebviewBrand();
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        setWebviewBrand();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [brands, location.pathname]);

  // Optimization: Fetch monthly_video_count when selectedBrand changes
  useEffect(() => {
    if (!selectedBrand) {
      setSelectedBrandHasVideoCount(null);
      setSelectedBrandVideoCount(null);
      return;
    }

    const fetchBrandVideoCount = async () => {
      try {
        const fullBrandData: BrandResponse = await brandClient.get(selectedBrand.id);
        const monthlyVideoCount = fullBrandData.brand_metadata?.monthly_video_count;

        if (monthlyVideoCount && monthlyVideoCount.trim() !== '') {
          setSelectedBrandHasVideoCount(true);
          setSelectedBrandVideoCount(monthlyVideoCount);
        } else {
          setSelectedBrandHasVideoCount(false);
          setSelectedBrandVideoCount(null);
        }
      } catch (error) {
        // Set to null to indicate unknown state, fallback to old behavior if needed
        setSelectedBrandHasVideoCount(null);
        setSelectedBrandVideoCount(null);
      }
    };

    fetchBrandVideoCount();
  }, [selectedBrand]);

  // Listen for brand data updates from Company Information Card to fix modal gating bug
  useEffect(() => {
    const handleBrandDataUpdate = (event: CustomEvent) => {
      const { brandId, hasVideoCount } = event.detail;

      // Only update if this event is for the currently selected brand
      if (selectedBrand && selectedBrand.id === brandId) {
        setSelectedBrandHasVideoCount(hasVideoCount);
        if (!hasVideoCount) {
          setSelectedBrandVideoCount(null);
        }
      }
    };

    window.addEventListener('brandDataUpdated', handleBrandDataUpdate as EventListener);

    return () => {
      window.removeEventListener('brandDataUpdated', handleBrandDataUpdate as EventListener);
    };
  }, [selectedBrand]);

  const value: BrandSelectionContextType = {
    brands,
    brandsLoading,
    selectedBrand,
    setSelectedBrand,
    isLoading,
    setIsLoading,
    isInitialized: hasInitialized,
    selectedBrandHasVideoCount,
    selectedBrandVideoCount,
  };

  return <BrandSelectionContext.Provider value={value}>{children}</BrandSelectionContext.Provider>;
};

export const useBrandSelection = (): BrandSelectionContextType => {
  const context = useContext(BrandSelectionContext);
  if (context === undefined) {
    throw new Error('useBrandSelection must be used within a BrandSelectionProvider');
  }
  return context;
};
